import './sp-spot-transfer-indicator-dialog/sp-spot-transfer-indicator-dialog';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';
import '@shoelace-style/shoelace/dist/components/progress-ring/progress-ring.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';

import {SpDefaultDialog, SpDefaultDialogButtonConfig} from '../sp-dialog/sp-default-dialog';
import {html, LitElement, PropertyValues} from 'lit';
import {styles} from './sp-spot-transfer-indicator.styles';
import {customElement, property, query, state} from 'lit/decorators.js';
import {translate, translateUppercase} from "@stefanholzapfel/lit-translate";
import {styleMap} from "lit-html/directives/style-map.js";
import {when} from "lit/directives/when.js";
import {SpUiConfigService} from '../../services/sp-ui-config.service';
import {SpImageService} from "../../services/sp-image.service";
import {SpSpotTransferIndicatorAction, SpTransferringSpot, SpTransferStatus} from "./sp-spot-transfer-indicator.types";
import {animate, fadeIn, fadeOut} from "../../controllers/animate";

@customElement('sp-spot-transfer-indicator')
export class SpSpotTransferIndicator extends LitElement {
    @property()
    transferringSpots: SpTransferringSpot[] = [];
    @property({type: Boolean})
    allowQueue = true;
    @property({type: Boolean})
    allowClearEmptyList = false;

    @state()
    activeTransfers: SpTransferringSpot[] = [];
    @state()
    overallProgress = 0;
    @state()
    erroneousCount = 0;
    @state()
    pausedCount = 0;
    @state()
    finishedCount = 0;
    @state()
    abortedCount = 0;
    @state()
    postProcessingCount = 0;
    @state()
    latestSpotImageUrl;
    latestSpotId;

    @query('#dialog') dialog: SpDefaultDialog;

    static styles = [
        styles
    ];

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        // Only needed for legacy reasons in the spotteron-app, which uses fastclick.js and cannot set classes for nested components
        this.shadowRoot.host.classList.add('needsclick');
    }

    protected willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        this.activeTransfers = this.transferringSpots.filter(_spot => _spot.status !== SpTransferStatus.Aborted);
        this.erroneousCount = this.transferringSpots.filter(spot => spot.status === SpTransferStatus.Erroneous).length;
        this.finishedCount = this.transferringSpots.filter(spot => spot.status === SpTransferStatus.Finished).length;
        this.abortedCount = this.transferringSpots.filter(spot => spot.status === SpTransferStatus.Aborted).length;
        this.pausedCount = this.transferringSpots.filter(spot => spot.status === SpTransferStatus.Paused).length;
        this.postProcessingCount = this.transferringSpots.filter(spot => spot.status === SpTransferStatus.PostProcessing).length;
        this.overallProgress = this.activeTransfers.length === 0 ?
            0 : Math.floor((this.activeTransfers.reduce((acc, spot) => acc + spot.progress, 0) / this.activeTransfers.length) * 100);
        this.createLatestUploadImageUrl();
        this.requestUpdate();
    }

    protected render() {
        return html`
            <sp-default-dialog id="dialog"
                               alignBody="center"
                               .defaultClearContentOnHide=${false}
                               .headerText=${translate('UPLOADS_TITLE')}
                               .template=${html`
                                   <sp-spot-transfer-indicator-dialog
                                           .clickActionHandler=${this.clickActionHandler.bind(this)}
                                           .transferringSpots=${[...this.transferringSpots]}></sp-spot-transfer-indicator-dialog>`}
                                           .allowQueue=${ this.allowQueue }
                               .buttons=${this.getDialogButtons()}></sp-default-dialog>
            ${
                when(this.transferringSpots.length > 0, () => {
                    return html`<div class="container" 
                             @click=${this.indicatorClick}
                             ${ () => {
                                 try { animate({
                                    in: fadeIn,
                                    out: fadeOut,
                                }) } catch(e) {}}
                             }>
                            <sl-progress-ring .value=${this.overallProgress}></sl-progress-ring>
                            <div class="bg" style=${styleMap({'background-image': this.latestSpotImageUrl ? this.latestSpotImageUrl : `url(${SpImageService.getPlaceholderUrl('spots')})`})}></div>
                            <sl-badge .variant=${this.erroneousCount > 0 ? 'danger' : 'primary'} pill
                                      ?pulse=${this.erroneousCount > 0}>${this.getBadgeContent()}
                            </sl-badge>
                        </div>`})
            }`;
    }

    createLatestUploadImageUrl() {
        if (this.activeTransfers.length > 0) {
            const latestSpot = this.activeTransfers[this.activeTransfers.length - 1];
            if (latestSpot.id !== this.latestSpotId) {
                this.latestSpotId = latestSpot.id;
                if (this.latestSpotImageUrl) URL.revokeObjectURL(this.latestSpotImageUrl);
                if (latestSpot.image) {
                    'path' in latestSpot.image ?
                        this.latestSpotImageUrl = `url(${ SpImageService.buildUrl(latestSpot.image.spec.type, 'thumbs', latestSpot.image.path, 'x200', )}` :
                        this.latestSpotImageUrl = `url(${URL.createObjectURL(latestSpot.image.blob)})`;
                } else {
                    this.latestSpotImageUrl = null;
                }
            }
        } else { this.latestSpotImageUrl = null; }
    }

    getBadgeContent() {
        if (this.erroneousCount > 0) return html`<div class="icon error"></div>`;
        if (this.activeTransfers.length - this.finishedCount === 0) return html`<div class="icon finished"></div>`;
        if (this.pausedCount > 0 && this.pausedCount === this.activeTransfers.length && this.postProcessingCount === 0) return html`<div class="icon pause"></div>`;
        if (this.postProcessingCount > 0 && this.activeTransfers.length - (this.pausedCount + this.finishedCount) === this.postProcessingCount) return html`<sl-spinner></sl-spinner>`;
        return html`<span class="text-container">${this.overallProgress}<span class="smaller">%</span></span>`;
    }

    getDialogButtons(): SpDefaultDialogButtonConfig[] {
        return [
            {
                clickCallback: () => {
                    this.dialog.hide();
                },
                text: translateUppercase('UPLOADS_HIDE'),
                textPosition: "center"
            },
            {
                clickCallback: () => {
                    const clearableSpots = this.transferringSpots.filter(_spot => _spot.status === SpTransferStatus.Aborted || _spot.status === SpTransferStatus.Finished ).map(_spot => _spot.id);
                    this.clickActionHandler('remove', clearableSpots);
                },
                disabled: this.abortedCount + this.finishedCount === 0 && !this.allowClearEmptyList,
                text: translateUppercase('UPLOADS_CLEAR'),
                textPosition: "center"
            }
        ];
    }

    indicatorClick(e) {
        this.dialog.show();
    }

    clickActionHandler(action: SpSpotTransferIndicatorAction, spotIds: string[]) {
        if (action === 'browse' || (action === 'remove' && this.transferringSpots.length === spotIds.length && !this.allowClearEmptyList)) {
            this.dialog.hide();
        }
        this.dispatchEvent(new SpSpotTransferIndicatorActionEvent({
            detail: {
                action,
                spotIds
            },
            bubbles: true,
            composed: true
        }));
    }
}

export interface SpSpotTransferIndicatorActionEventPayload {
    action: SpSpotTransferIndicatorAction;
    spotIds: string[];
}
export class SpSpotTransferIndicatorActionEvent extends CustomEvent<SpSpotTransferIndicatorActionEventPayload> {
    constructor(options: CustomEventInit<SpSpotTransferIndicatorActionEventPayload>) {
        super('click-action', options);
    }
}
